import type { FeaturePermissionLevel } from '../../types/security';

export const FEATURE_PERMISSION_RANK: Record<FeaturePermissionLevel, number> = {
  none: 0,
  view: 1,
  edit_own: 2,
  edit_all: 3,
};

export interface FeaturePermissionDefinition {
  feature_key: string;
  parent_page_key: string;
  label: string;
  description: string;
  group: string;
}

export const MAINTENANCE_ROUTE_FEATURES: Record<string, string> = {
  forms: 'maintenance.forms',
  'venue-precontract-form': 'maintenance.forms',
  'items-panel-columns': 'maintenance.items_panel_columns',
  assets: 'maintenance.assets',
  stock: 'maintenance.stock',
  categories: 'maintenance.categories',
  charges: 'maintenance.charges',
  'event-stages': 'maintenance.event_stages',
  items: 'maintenance.items',
  'items/installation': 'maintenance.items',
  kits: 'maintenance.kits',
  labor: 'maintenance.labor',
  pricing: 'maintenance.pricing',
  service: 'maintenance.service',
  'accounting-gl': 'maintenance.accounting_gl',
  'purchase-orders': 'maintenance.purchase_orders',
  'xrental-orders': 'maintenance.xrental_orders',
  vendors: 'maintenance.vendors',
  locations: 'maintenance.locations',
  'items-nonserial': 'maintenance.nonserial_items',
  'payment-terms': 'maintenance.payment_terms',
  products: 'maintenance.products',
  sections: 'maintenance.sections',
  'tax-codes': 'maintenance.tax_codes',
  'terms-and-conditions': 'maintenance.terms_and_conditions',
  users: 'maintenance.users',
  freelancers: 'maintenance.freelancers',
};

export const MAINTENANCE_FEATURES: FeaturePermissionDefinition[] = [
  {
    feature_key: 'maintenance.forms',
    parent_page_key: 'maintenance',
    label: 'Forms Library',
    description: 'Build and publish reusable intake forms.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.items_panel_columns',
    parent_page_key: 'maintenance',
    label: 'Event Panel Columns',
    description: 'Configure event panel columns and formulas.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.assets',
    parent_page_key: 'maintenance',
    label: 'Manage Assets',
    description: 'Track serialized assets, service status, and maintenance history.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.stock',
    parent_page_key: 'maintenance',
    label: 'Manage Cables & Adapters',
    description: 'Oversee cable stock, kits, and adapter inventory levels.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.categories',
    parent_page_key: 'maintenance',
    label: 'Manage Categories',
    description: 'Define catalog categories and taxonomy.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.charges',
    parent_page_key: 'maintenance',
    label: 'Manage Charge',
    description: 'Maintain charge-only catalog items.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.event_stages',
    parent_page_key: 'settings',
    label: 'Manage Event Stages',
    description: 'Assign order stages to Events library filter groups.',
    group: 'Settings tabs',
  },
  {
    feature_key: 'maintenance.items',
    parent_page_key: 'maintenance',
    label: 'Manage Items',
    description: 'Maintain rentable item catalog and availability settings.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.kits',
    parent_page_key: 'maintenance',
    label: 'Manage Kits',
    description: 'Build and maintain kit bundles.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.labor',
    parent_page_key: 'maintenance',
    label: 'Manage Labor',
    description: 'Update labor roles, rate cards, and scheduling defaults.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.pricing',
    parent_page_key: 'maintenance',
    label: 'Manage Pricing',
    description: 'Manage location pricing for items, kits, charges, and labor.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.service',
    parent_page_key: 'maintenance',
    label: 'Manage Service',
    description: 'Configure external service tickets, agreement tiers, PM visits, service charges, and labor.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.accounting_gl',
    parent_page_key: 'maintenance',
    label: 'Accounting GL',
    description: 'Manage GL defaults and item-specific accounting overrides.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.purchase_orders',
    parent_page_key: 'transfers',
    label: 'Purchase Orders',
    description: 'Create, receive, and review procurement orders.',
    group: 'Warehouse procurement',
  },
  {
    feature_key: 'maintenance.xrental_orders',
    parent_page_key: 'transfers',
    label: 'Subrentals',
    description: 'Equipment and vehicles rented from vendors: receive, return and extend.',
    group: 'Warehouse procurement',
  },
  {
    feature_key: 'maintenance.vendors',
    parent_page_key: 'maintenance',
    label: 'Vendors',
    description: 'Maintain vendor and supplier directory.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.locations',
    parent_page_key: 'maintenance',
    label: 'Manage Locations',
    description: 'Maintain locations, regional assignments, and delivery details.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.nonserial_items',
    parent_page_key: 'maintenance',
    label: 'Manage Non Serial Items',
    description: 'Focused catalog view for nonserial items.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.payment_terms',
    parent_page_key: 'maintenance',
    label: 'Manage Payment Terms',
    description: 'Manage payment terms used for clients and branches.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.products',
    parent_page_key: 'maintenance',
    label: 'Manage Products',
    description: 'Configure product families and catalog metadata.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.sections',
    parent_page_key: 'maintenance',
    label: 'Manage Sections',
    description: 'Control quote and order sections.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.tax_codes',
    parent_page_key: 'maintenance',
    label: 'Manage Tax Region',
    description: 'Define tax codes, rates, and application rules.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.terms_and_conditions',
    parent_page_key: 'maintenance',
    label: 'Terms & Conditions',
    description: 'Manage default and custom terms.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.users',
    parent_page_key: 'maintenance',
    label: 'User Directory',
    description: 'Invite team members and manage access.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.freelancers',
    parent_page_key: 'maintenance',
    label: 'Freelancers',
    description: 'Manage freelancer profiles, offers, bans, and notes.',
    group: 'Maintenance cards',
  },
];

/**
 * Post-event questionnaires (2026-10-22): the five granular permissions migration
 * 20261022200000_post_event_questionnaires inserts into feature_definitions, with the same labels, descriptions and
 * group labels. The two maintenance ones are not Maintenance cards (no route), so they stay out of MAINTENANCE_FEATURES.
 */
export const POST_EVENT_FEATURES: FeaturePermissionDefinition[] = [
  {
    feature_key: 'events.post_event',
    parent_page_key: 'events',
    label: 'Post-event questionnaires',
    description: "Copy and send an event's questionnaire links, change its reminders, and turn links off. Anyone who can open the event sees the answers.",
    group: 'Post-event',
  },
  {
    feature_key: 'events.post_event.review',
    parent_page_key: 'events',
    label: 'Post-event review',
    description: 'See the private management review, resolve or acknowledge concerns, and add notes.',
    group: 'Post-event',
  },
  {
    feature_key: 'events.post_event.approve',
    parent_page_key: 'events',
    label: 'Approve post-event review',
    description: 'Approve a review once every concern is resolved or acknowledged, or reopen it.',
    group: 'Post-event',
  },
  {
    feature_key: 'maintenance.forms.post_event',
    parent_page_key: 'maintenance',
    label: 'Post-event questionnaires',
    description: 'Build, publish and archive post-event questionnaires in Forms.',
    group: 'Maintenance cards',
  },
  {
    feature_key: 'maintenance.locations.post_event',
    parent_page_key: 'maintenance',
    label: 'Post-event settings',
    description: "Turn a location's questionnaires on or off and set its minimums, Required and reminders. Only the location's regional managers use it; admins and super admins always can.",
    group: 'Maintenance cards',
  },
];

export const FEATURE_PERMISSION_DEFINITIONS: FeaturePermissionDefinition[] = [
  ...MAINTENANCE_FEATURES,
  ...POST_EVENT_FEATURES,
  {
    feature_key: 'ai.contracts.venue',
    parent_page_key: 'venue_contracts',
    label: 'Venue Contract AI',
    description: 'Search active venue agreements and addenda from AI Chat with protected, page-aware citations.',
    group: 'Venue contracts',
  },
  {
    feature_key: 'service.tickets',
    parent_page_key: 'service',
    label: 'Service Tickets',
    description: 'View and edit service tickets; edit own limits edits to tickets assigned to the user or their home site.',
    group: 'Service',
  },
  {
    feature_key: 'service.scan',
    parent_page_key: 'service',
    label: 'Service Scan',
    description: 'Scan assets into service workflows.',
    group: 'Service',
  },
];

const MAINTENANCE_FEATURE_KEY_SET = new Set(MAINTENANCE_FEATURES.map((feature) => feature.feature_key));

export function isKnownMaintenanceFeatureKey(featureKey: string): boolean {
  return MAINTENANCE_FEATURE_KEY_SET.has(featureKey);
}
